/* Morgan Mountain. Native commerce navigation; small progressive enhancements. */
(() => {
  'use strict';
  if (window.MorganMountain) return;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const recommendationsLoaded = new WeakSet();
  let headerObserver;
  const returnFocus = new WeakMap();
  const motionAllowed = () => !motionQuery.matches && document.querySelector('meta[name="mm-motion"]')?.content !== 'off';
  const syncMotion = () => document.documentElement.classList.toggle('mm-motion', motionAllowed());
  const reveals = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (motionAllowed()) entry.target.classList.add('mm-in-view');
      reveals.unobserve(entry.target);
    });
  }, { threshold: 0.08 }) : null;

  function openDialog(dialog, opener) {
    if (!dialog || dialog.open) return;
    returnFocus.set(dialog, opener);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else { dialog.setAttribute('open', ''); dialog.setAttribute('aria-modal', 'true'); }
    opener?.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    dialog.querySelector('[data-mm-close]')?.focus();
  }
  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else { dialog.removeAttribute('open'); afterClose(dialog); }
  }
  function afterClose(dialog) {
    const opener = returnFocus.get(dialog);
    opener?.setAttribute('aria-expanded', 'false');
    if (!document.querySelector('[data-mm-dialog][open]')) document.documentElement.style.overflow = '';
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }
  function setupHeader() {
    headerObserver?.disconnect();
    const header = document.querySelector('[data-mm-header]');
    const hero = document.querySelector('[data-mm-hero]');
    if (!header) return;
    header.classList.remove('is-over-hero');
    if (!hero || !('IntersectionObserver' in window)) return;
    const height = header.offsetHeight;
    const update = rect => header.classList.toggle('is-over-hero', rect.top <= height && rect.bottom > height);
    update(hero.getBoundingClientRect());
    headerObserver = new IntersectionObserver(entries => update(entries[0].boundingClientRect), { rootMargin: `-${height}px 0px 0px 0px`, threshold: 0 });
    headerObserver.observe(hero);
  }

  function setupDesktopHoverNav(root = document) {
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    root.querySelectorAll('.mm-desktop-nav').forEach(nav => {
      if (nav.dataset.mmHoverReady === 'true') return;
      nav.dataset.mmHoverReady = 'true';
      const closeAll = except => {
        nav.querySelectorAll('details[open]').forEach(details => {
          if (details !== except) {
            details.open = false;
            details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
          }
        });
      };
      const openDetails = details => {
        if (!details) return;
        closeAll(details);
        details.open = true;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      };
      nav.querySelectorAll('details').forEach(details => {
        const summary = details.querySelector('summary');
        summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        summary?.addEventListener('click', () => {
          setTimeout(() => {
            if (details.open) closeAll(details);
            summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          }, 0);
        });
        if (hoverable) {
          details.addEventListener('mouseenter', () => openDetails(details));
          details.addEventListener('focusin', () => openDetails(details));
        }
      });
      if (hoverable) {
        nav.querySelectorAll(':scope > ul > li').forEach(item => {
          item.addEventListener('mouseenter', () => {
            const details = item.querySelector(':scope > details');
            if (details) openDetails(details);
            else closeAll();
          });
        });
        nav.addEventListener('mouseleave', () => closeAll());
      }
    });
  }

  async function refreshCart() {
    const cartURL = document.querySelector('meta[name="mm-cart-url"]')?.content;
    if (!cartURL) return;
    try {
      const response = await fetch(`${cartURL}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-mm-cart-count]').forEach(el => { el.textContent = String(cart.item_count); });
      document.querySelectorAll('[data-mm-cart-link]').forEach(el => el.setAttribute('aria-label', `Bag, ${cart.item_count} items`));
    } catch (_) { /* Server-rendered count remains intact. */ }
  }
  async function loadRecommendations(element) {
    if (recommendationsLoaded.has(element)) return;
    recommendationsLoaded.add(element);
    try {
      const response = await fetch(element.dataset.mmUrl, { credentials: 'same-origin' });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-mm-recommendations]');
      if (!element.isConnected || !content?.querySelector('.mm-card')) return;
      element.replaceChildren(...content.childNodes);
      element.hidden = false;
      initReveals(element);
      document.dispatchEvent(new CustomEvent('mm:content:loaded', { detail: { root: element } }));
    } catch (_) { /* Empty and failed recommendations stay hidden. */ }
  }
  function initReveals(root) {
    root.querySelectorAll('[data-mm-reveal]').forEach(el => {
      if (seen.has(el)) return;
      seen.add(el);
      reveals?.observe(el);
    });
  }
  function init(root = document) {
    syncMotion();
    initReveals(root);
    setupHeader();
    setupDesktopHoverNav(root);
    root.querySelectorAll('[data-mm-recommendations]').forEach(loadRecommendations);
    root.querySelectorAll('[data-mm-dialog]').forEach(dialog => {
      if (dialog.dataset.mmReady) return;
      dialog.dataset.mmReady = 'true';
      dialog.addEventListener('close', () => afterClose(dialog));
    });
    const models = root.querySelectorAll('model-viewer');
    if (models.length && window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: error => {
        if (error || !window.Shopify.ModelViewerUI) return;
        models.forEach(model => { if (!model.dataset.mmReady) { model.dataset.mmReady = 'true'; new window.Shopify.ModelViewerUI(model); } });
      } }]);
    }
  }
  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-mm-open]');
    if (opener) { openDialog(document.getElementById(opener.dataset.mmOpen), opener); return; }
    const closer = event.target.closest('[data-mm-close]');
    if (closer) { closeDialog(closer.closest('dialog')); return; }
    if (event.target.matches('[data-mm-dialog]')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(event.target);
    }
    const zoom = event.target.closest('[data-mm-zoom]');
    if (zoom) {
      const dialog = document.getElementById(zoom.dataset.mmZoom);
      if (!dialog) return;
      const img = new Image(); img.src = zoom.dataset.mmSrc; img.alt = zoom.dataset.mmAlt || ''; img.decoding = 'async';
      dialog.querySelector('[data-mm-zoom-content]').replaceChildren(img);
      openDialog(dialog, zoom); return;
    }
    const galleryLink = event.target.closest('[data-mm-gallery-link]');
    if (galleryLink) {
      const media = document.getElementById(galleryLink.hash.slice(1));
      if (media) { event.preventDefault(); media.scrollIntoView({ behavior: motionAllowed() ? 'smooth' : 'auto', block: 'nearest', inline: 'start' }); media.focus({ preventScroll: true }); }
    }
    const videoToggle = event.target.closest('[data-mm-video-toggle]');
    if (videoToggle) {
      const hero = videoToggle.closest('[data-mm-hero]');
      let video = hero.querySelector('video');
      if (!video) { const template = hero.querySelector('[data-mm-hero-video]'); template.parentElement.append(template.content.cloneNode(true)); video = hero.querySelector('video'); }
      if (!video) return;
      if (video.paused) {
        video.play().then(() => { videoToggle.textContent = 'Pause background video'; videoToggle.setAttribute('aria-pressed', 'true'); }).catch(() => { videoToggle.textContent = 'Play background video'; });
      } else { video.pause(); videoToggle.textContent = 'Play background video'; videoToggle.setAttribute('aria-pressed', 'false'); }
    }
  });
  document.addEventListener('keydown', event => {
    const dialog = document.querySelector('[data-mm-dialog][open]');
    if (dialog && typeof dialog.showModal !== 'function') {
      if (event.key === 'Escape') closeDialog(dialog);
      if (event.key === 'Tab') {
        const focusables = [...dialog.querySelectorAll('a[href],button,input,select,textarea,summary')].filter(el => !el.disabled && el.getClientRects().length);
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }
    if (event.key === 'Escape' && !dialog) document.querySelectorAll('.mm-desktop-nav details[open]').forEach(details => { details.open = false; details.querySelector('summary')?.focus(); });
  });
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-mm-plan]')) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('option_values');
    url.searchParams.set('variant', event.target.dataset.mmVariant);
    if (event.target.value) url.searchParams.set('selling_plan', event.target.value);
    else url.searchParams.delete('selling_plan');
    event.target.closest('form').querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = true; });
    window.location.assign(url.href);
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-mm-cart-form]');
    if (!form) return;
    form.querySelectorAll('[data-mm-cart-express]').forEach(el => { el.hidden = true; });
    form.querySelectorAll('[data-mm-cart-dirty]').forEach(el => { el.hidden = false; });
    const status = document.querySelector('[data-mm-cart-status]');
    if (status) status.textContent = 'Changes are not saved. Select Update bag to refresh totals, or Checkout to continue with these changes.';
  });
  document.addEventListener('submit', event => {
    if (event.target.matches('[data-mm-cart-form]') && event.submitter?.name === 'update') {
      try { sessionStorage.setItem('mm-cart-updated', '1'); } catch (_) { /* Storage may be unavailable. */ }
    }
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-mm-dialog][open]').forEach(closeDialog);
    event.target.querySelectorAll('[data-mm-reveal]').forEach(el => reveals?.unobserve(el));
    requestAnimationFrame(setupHeader);
  });
  document.addEventListener('mm:cart:refresh', refreshCart);
  window.addEventListener('pageshow', refreshCart);
  window.addEventListener('focus', refreshCart);
  motionQuery.addEventListener('change', () => {
    syncMotion();
    if (motionQuery.matches) document.querySelectorAll('[data-mm-hero] video').forEach(video => {
      video.pause(); const button = video.closest('[data-mm-hero]').querySelector('[data-mm-video-toggle]');
      if (button) { button.textContent = 'Play background video'; button.setAttribute('aria-pressed', 'false'); }
    });
  });
  const start = () => {
    init();
    try {
      if (document.querySelector('[data-mm-cart-status]') && sessionStorage.getItem('mm-cart-updated')) {
        sessionStorage.removeItem('mm-cart-updated');
        setTimeout(() => { document.querySelector('[data-mm-cart-status]').textContent = 'Bag updated. Review your quantities and totals below.'; }, 100);
      }
    } catch (_) { /* No storage dependency for shopping. */ }
  };
  window.MorganMountain = { init, refreshCart };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

/* Email popup and product zoom helpers. */
(() => {
  const storageKey = 'mmEmailPopupDismissed';
  const popup = document.querySelector('[data-mm-email-popup]');
  const showPopup = () => {
    if (!popup || popup.dataset.mmShown === 'true') return;
    try { if (localStorage.getItem(storageKey) === '1') return; } catch (_) {}
    popup.hidden = false;
    popup.dataset.mmShown = 'true';
    popup.querySelector('input[type="email"]')?.focus({ preventScroll: true });
  };
  if (popup) {
    popup.querySelector('[data-mm-email-popup-close]')?.addEventListener('click', () => {
      popup.hidden = true;
      try { localStorage.setItem(storageKey, '1'); } catch (_) {}
    });
    popup.addEventListener('click', event => {
      if (event.target === popup) {
        popup.hidden = true;
        try { localStorage.setItem(storageKey, '1'); } catch (_) {}
      }
    });
    setTimeout(showPopup, 8000);
    document.addEventListener('mouseout', event => {
      if (event.clientY <= 0) showPopup();
    }, { once: true });
  }
  document.addEventListener('click', event => {
    const lightboxImage = event.target.closest('.mm-lightbox img');
    if (lightboxImage) lightboxImage.classList.toggle('mm-zoomed');
  });
})();
